import { useUserRole } from "@/api";
import { SidebarVersionIndicator } from "@/features/client-release";
import { OnboardingSidebarIcon } from "@/features/onboarding";
import { HiddenFeature } from "@/lib/unleash/HiddenFeature";
import { cn } from "@/lib/utils";
import { SIMULATE_ENERGY_MACHINES_CUSTOM_URL_HOST } from "@/lib/white-label-branding/BrandLabelConstants";
import { WhiteLabelBrandSidebarLogo } from "@/lib/white-label-branding/WhiteLabelBrandLogo";
import { getWhiteLabelBrand } from "@/lib/white-label-branding/getWhiteLabelBrand";
import { useFlag } from "@unleash/proxy-client-react";
import {
  BookMarked,
  Boxes,
  Building,
  MailPlus,
  MessageCircleQuestion,
  Pin,
  Share2,
  Users
} from "lucide-react";
import { useState } from "react";
import { useLocalStorage } from "usehooks-ts";
import TooltipHover from "../ui/TooltipHover";
import Dialog from "../ui/dialog";
import Feedback from "../ui/feedback";
import { ThemeToggle } from "../ui/themeToggle";
import { OrganizationDropdown } from "./OrganizationDropdown";
import { SidebarLinksExternal, SidebarLinksLocal } from "./SidebarButtons";
import { SidebarUser } from "./SidebarUser";

const iconSize = "h-[20px] w-[20px]";
const iconWeight = 1.6;

function Sidebar({ canMinimize = false }: { canMinimize?: boolean }) {
  const [isOpen, setIsOpen] = useState(!canMinimize);
  const showPinned = useFlag("show-pinned");
  const brand = getWhiteLabelBrand();
  const [latestOrganizationSlug] = useLocalStorage<string | null>(
    "latestOrganizationSlug",
    null
  );
  const { isAdmin } = useUserRole();

  return (
    <div
      className={cn(
        "dark:bg-brand-gray-950 bg-white flex flex-col items-center rounded-lg text-brand-gray-700 dark:text-brand-gray-100 border dark:border-brand-gray-900 transition-all",
        isOpen
          ? "h-[calc(100vh-12px)]"
          : "h-fit border-brand-gray-300 hover:border-brand-primary dark:hover:bg-brand-gray-900",
        canMinimize && "border-brand-gray-200"
      )}
    >
      {isOpen && (
        <div className="flex h-full flex-col items-center justify-between mb-5 mt-4">
          <div className="flex flex-col items-center gap-5">
            {latestOrganizationSlug && (
              <>
                <SidebarLinksLocal
                  tooltipText="App Gallery"
                  route="/organization/$organizationSlug/gallery"
                  params={{ organizationSlug: latestOrganizationSlug }}
                  icon={<Boxes strokeWidth={iconWeight} className={iconSize} />}
                />

                <OrganizationDropdown
                  activeOrganizationSlug={latestOrganizationSlug}
                />

                {isAdmin && (
                  <SidebarLinksLocal
                    tooltipText="Members"
                    route="/organization/$organizationSlug/organization-management"
                    params={{ organizationSlug: latestOrganizationSlug }}
                    icon={
                      <Users strokeWidth={iconWeight} className={iconSize} />
                    }
                  />
                )}
                {showPinned && (
                  <TooltipHover text="Pinned">
                    <Pin
                      strokeWidth={iconWeight}
                      className={cn(
                        iconSize,
                        "transition-colors text-brand-gray-400 hover:text-brand-gray-900  dark:text-brand-gray-300 dark:hover:text-brand-gray-100"
                      )}
                    />
                  </TooltipHover>
                )}
                {isAdmin && (
                  <SidebarLinksLocal
                    tooltipText="Subscriptions"
                    route="/organization/$organizationSlug/subscriptions"
                    params={{ organizationSlug: latestOrganizationSlug }}
                    icon={
                      <Share2 strokeWidth={iconWeight} className={iconSize} />
                    }
                  />
                )}
              </>
            )}
            {!latestOrganizationSlug && (
              <SidebarLinksLocal
                tooltipText="Organization"
                route="/organization"
                icon={
                  <Building strokeWidth={iconWeight} className={iconSize} />
                }
              />
            )}
          </div>
          <div className="flex flex-col items-center gap-5">
            <HiddenFeature featureFlag="is-numerous-employee">
              <SidebarLinksLocal
                tooltipText="Internal mail platform"
                route="/internal/send-user-emails"
                icon={
                  <MailPlus strokeWidth={iconWeight} className={iconSize} />
                }
              />
            </HiddenFeature>
            <HiddenFeature featureFlag="">
              <ThemeToggle />
            </HiddenFeature>
            {isAdmin &&
              latestOrganizationSlug &&
              brand?.hostname !== SIMULATE_ENERGY_MACHINES_CUSTOM_URL_HOST && (
                <SidebarLinksLocal
                  tooltipText="Onboarding"
                  route="/organization/$organizationSlug/onboarding"
                  params={{ organizationSlug: latestOrganizationSlug }}
                  icon={
                    <OnboardingSidebarIcon
                      strokeWidth={iconWeight}
                      className={iconSize}
                    />
                  }
                />
              )}
            <SidebarLinksExternal
              tooltipText="Documentation"
              route="https://www.numerous.com/docs"
              icon={
                <BookMarked strokeWidth={iconWeight} className={iconSize} />
              }
            />
            <TooltipHover text="Report Issue">
              <Feedback>
                {() => (
                  <Dialog.Trigger asChild>
                    <div className="transition-colors hover:text-brand-gray-900 dark:hover:text-brand-gray-50">
                      <MessageCircleQuestion
                        strokeWidth={iconWeight}
                        className={iconSize}
                      />
                    </div>
                  </Dialog.Trigger>
                )}
              </Feedback>
            </TooltipHover>
            <SidebarUser />
          </div>
        </div>
      )}
      <button
        className={cn(
          "transition-all",
          isOpen
            ? "px-4 pb-4 h-auto w-[52px]"
            : "p-3.5 h-[48px] w-[48px] hover:text-brand-primary",
          !canMinimize && "cursor-default"
        )}
        onClick={canMinimize ? () => setIsOpen((prev) => !prev) : undefined}
      >
        <WhiteLabelBrandSidebarLogo />
      </button>
      {isOpen && <SidebarVersionIndicator />}
    </div>
  );
}

export default Sidebar;
